import { type Def, setValue, useValue } from '@/lib/feature-flags';

interface DevPanelRowProps {
  def: Def;
}

/**
 * One registry entry rendered as a labelled control. Booleans get a switch,
 * numbers a slider/stepper, strings a text input, enums a select. Reactive
 * entries update live; `reload` entries reload the page on change (handled by
 * `setValue`), so a hint is shown.
 */
export function DevPanelRow({ def }: DevPanelRowProps) {
  const value = useValue(def);
  const controlId = `devp-${def.type}-${def.key}`;

  return (
    <div className="devp-row">
      <div className="devp-row__text">
        <label className="devp-row__label" htmlFor={controlId}>
          {def.label}
        </label>
        <p className="devp-row__desc">
          {def.description}
          {def.behavior === 'reload' && (
            <span className="devp-row__reload"> · reloads on change</span>
          )}
        </p>
      </div>
      <div className="devp-row__control">{renderControl(def, value, controlId)}</div>
    </div>
  );
}

function renderControl(def: Def, value: boolean | number | string, controlId: string) {
  switch (def.type) {
    case 'boolean': {
      const on = value === true;
      return (
        <button
          type="button"
          id={controlId}
          role="switch"
          aria-checked={on}
          aria-label={def.label}
          className={`devp-switch${on ? ' is-on' : ''}`}
          onClick={() => setValue(def, !on)}
        >
          <span className="devp-switch__thumb" />
        </button>
      );
    }
    case 'number': {
      const num = typeof value === 'number' ? value : def.default;
      const ranged = def.min !== undefined && def.max !== undefined;
      return (
        <div className="devp-number">
          {ranged && (
            <input
              type="range"
              id={controlId}
              className="devp-range"
              min={def.min}
              max={def.max}
              step={def.step ?? 1}
              value={num}
              aria-label={def.label}
              onChange={(e) => setValue(def, Number(e.target.value))}
            />
          )}
          <input
            type="number"
            id={ranged ? undefined : controlId}
            className="devp-number__input"
            min={def.min}
            max={def.max}
            step={def.step ?? 1}
            value={num}
            aria-label={def.label}
            onChange={(e) => setValue(def, Number(e.target.value))}
          />
        </div>
      );
    }
    case 'enum':
      return (
        <select
          id={controlId}
          className="devp-select"
          value={typeof value === 'string' ? value : def.default}
          aria-label={def.label}
          onChange={(e) => setValue(def, e.target.value)}
        >
          {def.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    case 'string':
      return (
        <input
          type="text"
          id={controlId}
          className="devp-text"
          value={typeof value === 'string' ? value : def.default}
          aria-label={def.label}
          onChange={(e) => setValue(def, e.target.value)}
        />
      );
  }
}
